import { useMemo, useState } from 'react';
import { demeterCompanyApi } from '../../../Apis/Apis';
import { AgreeToCompanyTermsAndConditionsRequest, DemeterPaymentType, DemeterUserModel, DemeterUserType } from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserType } from '../../../Redux/Slices/UserSlice';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import TextButton from '../../Components/Form/Buttons/TextButton';
import CheckboxInput from '../../Components/Form/Inputs/CheckboxInput';
import TextInput from '../../Components/Form/Inputs/TextInput';
import ComponentSubHeader from '../../Components/Headers/ComponentSubHeader';
import PageHeader from '../../Components/Headers/PageHeader';
import ActionModal from '../../Components/Modals/ActionModal/ActionModal';
import RegexValidators from '../../Core/Validation/RegexValidators';
import useLanguage from '../../Services/Language/useLanguageHook';
import CompanyBillingForm, { UpsertCompanyBillingRequest } from '../Administration/Companies/CompanyBillingForm';
import CompanyDetailsForm, { UpsertCompanyDetailsRequest } from '../Administration/Companies/CompanyDetailsForm';
import CompanyRegistrationAgreement from './CompanyRegistrationAgreement';
import styles from './SessionPage.module.scss';

export interface ICompanyRegistrationProps {
    user: DemeterUserModel | undefined;
    handleSubmit: () => void;
    handleCancel: () => void;
}

const CompanyRegistration: React.FC<ICompanyRegistrationProps> = (props: ICompanyRegistrationProps) => {
    const [translations] = useLanguage();
    const currentUserType = useApplicationSelector(selectUserType);
    const [requestDetail, setRequestDetail] = useState<UpsertCompanyDetailsRequest>({
        address1: '',
        address2: '',
        city: '',
        state: '',
        country: '',
        postalCode: '',
        phone: '',
        contactPerson: '',
    });
    const [isRequestDetailValid, setIsRequestDetailValid] = useState<boolean>(false);
    const [requestBilling, setRequestBilling] = useState<UpsertCompanyBillingRequest>({
        billingContactName: '',
        billingCompanyName: '',
        billingAddress1: '',
        billingAddress2: '',
        billingCity: '',
        billingState: '',
        billingCountry: '',
        billingPostalCode: '',
        billingPhone: '',
        billingFax: '',
        billingEmail: '',
        billingVatNumber: '',
        billingSameAsCompanyInformation: false,
    });
    const [isRequestBillingValid, setIsRequestBillingValid] = useState<boolean>(false);
    const [requestCompany, setRequestCompany] = useState({
        termsAndConditionsAccepted: false as boolean | null,
        paymentType: undefined as DemeterPaymentType | undefined,
        futuresAccountTransferConsent: false as boolean | null,
        futuresAccountNumber: null as string | null,
        futuresAccountCustomerName: null as string | null,
        futuresAccountTitle: null as string | null,
    });

    const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] = useState<boolean>(false);

    const isPaymentValid = useMemo(() => {
        if (!requestCompany) {
            return false;
        }

        return (
            requestCompany &&
            (requestCompany.paymentType === DemeterPaymentType.WireTransfer ||
                (requestCompany.paymentType === DemeterPaymentType.DeductionFromFuturesAccount &&
                    !!requestCompany.futuresAccountTransferConsent &&
                    !!requestCompany.futuresAccountNumber &&
                    !!requestCompany.futuresAccountCustomerName &&
                    !!requestCompany.futuresAccountTitle))
        );
    }, [requestCompany]);

    const isValid = useMemo(() => {
        if (!requestCompany || !isRequestBillingValid || !isRequestDetailValid || !requestDetail || !props.user?.demeterCompanyGuid) {
            return false;
        }

        return !!requestDetail.contactPerson && requestCompany.termsAndConditionsAccepted && isPaymentValid;
    }, [isRequestBillingValid, isRequestDetailValid, requestCompany, requestDetail, props.user?.demeterCompanyGuid, isPaymentValid]);

    const [isSaving, handleSubmit] = useApiWithoutAutoExecute(() => {
        if (!isValid) {
            return null;
        }

        const request: AgreeToCompanyTermsAndConditionsRequest = {
            demeterCompanyGuid: props.user?.demeterCompanyGuid!,
            address1: requestDetail.address1,
            address2: requestDetail.address2,
            city: requestDetail.city,
            state: requestDetail.state,
            country: requestDetail.country,
            postalCode: requestDetail.postalCode,
            phone: requestDetail.phone,
            contactPerson: requestDetail.contactPerson,
            billingCompanyName: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingCompanyName ?? '' : props.user?.companyName ?? '',
            billingContactName: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingContactName ?? '' : requestDetail.contactPerson,
            billingAddress1: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingAddress1 ?? '' : requestDetail.address1,
            billingAddress2: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingAddress2 ?? '' : requestDetail.address2,
            billingCity: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingCity ?? '' : requestDetail.city,
            billingState: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingState ?? '' : requestDetail.state,
            billingCountry: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingCountry ?? '' : requestDetail.country,
            billingPostalCode: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingPostalCode ?? '' : requestDetail.postalCode,
            billingPhone: !requestBilling.billingSameAsCompanyInformation ? requestBilling.billingPhone ?? '' : requestDetail.phone,
            billingFax: requestBilling.billingFax,
            billingEmail: requestBilling.billingEmail,
            billingVatNumber: requestBilling.billingVatNumber,
            billingSameAsCompanyInformation: requestBilling.billingSameAsCompanyInformation,
            termsAndConditionsAccepted: requestCompany.termsAndConditionsAccepted ?? false,
            paymentType: requestCompany.paymentType ?? undefined,
            futuresAccountTransferConsent: requestCompany.futuresAccountTransferConsent,
            futuresAccountNumber: requestCompany.futuresAccountNumber,
            futuresAccountCustomerName: requestCompany.futuresAccountCustomerName,
            futuresAccountTitle: requestCompany.futuresAccountTitle,
        };
        props.handleSubmit();
        return demeterCompanyApi.agreeToCompanyTermsAndConditions(request?.demeterCompanyGuid!, request);
    });

    const isBusinessOwner = useMemo(() => currentUserType === DemeterUserType.BusinessOwner, [currentUserType]);

    return (
        <>
            <div className={styles.master_page_container}>
                <PageHeader title={props.user?.companyName || ''} />
                <div className={styles.company_registration}>
                    <div className={styles.company_registration_description}>{translations.companyRegistration.description}</div>
                    <div className={styles.company_registration_form}>
                        <CompanyDetailsForm
                            companyDetails={requestDetail}
                            handleCompanyDetailsChange={setRequestDetail}
                            handleCompanyDetailsIsValid={setIsRequestDetailValid}
                        />
                        <ComponentSubHeader
                            title={translations.companyRegistration.subscriberBillingDetailsHeader}
                            containerClassName={styles.company_registration_text_subheader}
                        />
                        <CompanyBillingForm
                            userCompanyName={props.user && props.user.companyName ? props.user.companyName : null}
                            companyBilling={requestBilling}
                            handleCompanyBillingChange={setRequestBilling}
                            handleCompanyBillingIsValid={setIsRequestBillingValid}
                        />

                        <ComponentSubHeader
                            title={translations.companyRegistration.subscriberPaymentOptionsHeader}
                            containerClassName={styles.company_registration_text_subheader}
                        />
                        <div className={styles.company_registration_form_field}>
                            <div className={styles.company_registration_form_field_checkbox_container}>
                                <CheckboxInput
                                    isChecked={requestCompany.paymentType === DemeterPaymentType.WireTransfer}
                                    handleIsChecked={(isChecked) => {
                                        if (isChecked) {
                                            setRequestCompany({ ...requestCompany, paymentType: DemeterPaymentType.WireTransfer });
                                        } else {
                                            setRequestCompany({ ...requestCompany, paymentType: undefined });
                                        }
                                    }}
                                    track={{
                                        eventCategory: 'CompanyRegistration',
                                        eventAction: 'PaymentTypeChanged',
                                        eventValue: !!requestCompany.paymentType,
                                    }}
                                />
                                <span>{translations.companyRegistration.paymentWireTransfer}</span>
                            </div>
                        </div>
                        {requestCompany.paymentType === DemeterPaymentType.WireTransfer && (
                            <>
                                <div className={styles.company_registration_form_field}>
                                    <span className={styles.company_registration_text_preformatted}>
                                        {translations.companyRegistration.paymentWireTransferUS}
                                    </span>
                                </div>
                                <div className={styles.company_registration_form_field}>
                                    <span className={styles.company_registration_text_preformatted}>
                                        {translations.companyRegistration.paymentWireTransferEU}
                                    </span>
                                </div>
                            </>
                        )}

                        <div className={styles.company_registration_form_field}>
                            <div className={styles.company_registration_form_field_checkbox_container}>
                                <CheckboxInput
                                    isChecked={requestCompany.paymentType === DemeterPaymentType.DeductionFromFuturesAccount}
                                    handleIsChecked={(isChecked) => {
                                        if (isChecked) {
                                            setRequestCompany({ ...requestCompany, paymentType: DemeterPaymentType.DeductionFromFuturesAccount });
                                        } else {
                                            setRequestCompany({ ...requestCompany, paymentType: undefined });
                                        }
                                    }}
                                    track={{
                                        eventCategory: 'CompanyRegistration',
                                        eventAction: 'PaymentTypeChanged',
                                        eventValue: !!requestCompany.paymentType,
                                    }}
                                />
                                <span>{translations.companyRegistration.paymentAutomaticDeduction}</span>
                            </div>
                        </div>

                        {requestCompany.paymentType === DemeterPaymentType.DeductionFromFuturesAccount && (
                            <>
                                <div className={styles.company_registration_form_field}>
                                    <TextInput
                                        title={translations.companyRegistration.futuresAccountNumber}
                                        value={requestCompany.futuresAccountNumber}
                                        required={!isBusinessOwner}
                                        denoteRequired={!isBusinessOwner}
                                        handleTextChange={(value) => setRequestCompany({ ...requestCompany, futuresAccountNumber: value })}
                                        errorMessage={translations.companies.errors.requiredField}
                                        validation={RegexValidators.AlphaNumericMinimumLength2}
                                        track={{
                                            eventCategory: 'CompanyRegistration',
                                            eventAction: 'FuturesAccountNumberChanged',
                                            eventValue: requestCompany.futuresAccountNumber,
                                        }}
                                    />
                                </div>
                                <div className={styles.company_registration_form_field}>
                                    <TextInput
                                        title={translations.companyRegistration.futuresAccountCustomerName}
                                        value={requestCompany.futuresAccountCustomerName}
                                        required={!isBusinessOwner}
                                        denoteRequired={!isBusinessOwner}
                                        handleTextChange={(value) => setRequestCompany({ ...requestCompany, futuresAccountCustomerName: value })}
                                        errorMessage={translations.companies.errors.requiredField}
                                        validation={RegexValidators.AlphaNumericMinimumLength2}
                                        track={{
                                            eventCategory: 'CompanyRegistration',
                                            eventAction: 'FuturesAccountCustomerNameChanged',
                                            eventValue: requestCompany.futuresAccountCustomerName,
                                        }}
                                    />
                                </div>
                                <div className={styles.company_registration_form_field}>
                                    <TextInput
                                        title={translations.companyRegistration.futuresAccountTitle}
                                        value={requestCompany.futuresAccountTitle}
                                        required={!isBusinessOwner}
                                        denoteRequired={!isBusinessOwner}
                                        handleTextChange={(value) => setRequestCompany({ ...requestCompany, futuresAccountTitle: value })}
                                        errorMessage={translations.companies.errors.requiredField}
                                        validation={RegexValidators.AlphaNumericMinimumLength2}
                                        track={{
                                            eventCategory: 'CompanyRegistration',
                                            eventAction: 'FuturesAccountTitleChanged',
                                            eventValue: requestCompany.futuresAccountTitle,
                                        }}
                                    />
                                </div>
                                <div className={styles.company_registration_form_field}>
                                    <div className={styles.company_registration_form_field_checkbox_container}>
                                        <CheckboxInput
                                            isChecked={requestCompany.futuresAccountTransferConsent ?? false}
                                            handleIsChecked={(value) => setRequestCompany({ ...requestCompany, futuresAccountTransferConsent: value })}
                                            track={{
                                                eventCategory: 'CompanyRegistration',
                                                eventAction: 'FuturesAccountTransferConsentChanged',
                                                eventValue: !!requestCompany.futuresAccountTransferConsent,
                                            }}
                                        />
                                        <span>{translations.companyRegistration.futuresAccountTransferConsent}</span>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className={styles.company_registration_form_field}>
                            <div className={styles.company_registration_form_field_checkbox_container}>
                                <CheckboxInput
                                    isChecked={requestCompany.termsAndConditionsAccepted ?? false}
                                    handleIsChecked={(value) => setRequestCompany({ ...requestCompany, termsAndConditionsAccepted: value })}
                                    track={{
                                        eventCategory: 'CompanyRegistration',
                                        eventAction: 'TermsAndConditionsAccepted',
                                        eventValue: !!requestCompany.termsAndConditionsAccepted,
                                    }}
                                />
                                <span>
                                    {translations.companyRegistration.agreeTo}{' '}
                                    <TextButton
                                        title={translations.words.termsAndConditions}
                                        onClick={() => setShowTermsAndConditionsModal(true)}
                                        track={{ eventCategory: 'CompanyRegistration', eventAction: 'ShowTermsAndConditionsModalClicked' }}
                                    />
                                </span>
                            </div>
                        </div>
                        <div className={styles.company_registration_form_buttons}>
                            <LinkButton
                                title={translations.actions.cancel}
                                type={LinkButtonType.White}
                                onClick={props.handleCancel}
                                disabled={isSaving}
                                track={{ eventCategory: 'CompanyRegistration', eventAction: 'CancelClicked' }}
                            />
                            <LinkButton
                                title={translations.actions.submit}
                                type={LinkButtonType.Blue}
                                onClick={handleSubmit}
                                disabled={isSaving || !isValid}
                                track={{ eventCategory: 'CompanyRegistration', eventAction: 'SubmitClicked' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ActionModal
                showModal={showTermsAndConditionsModal}
                header={translations.companyRegistration.termsAndConditionsHeader}
                handleCancel={() => setShowTermsAndConditionsModal(false)}
                track={{ eventCategory: 'CompanyRegistration', eventAction: 'ModalTermsAndConditions' }}
            >
                <CompanyRegistrationAgreement
                    handleCancel={() => setShowTermsAndConditionsModal(false)}
                    handleAccept={() => {
                        setRequestCompany({ ...requestCompany!, termsAndConditionsAccepted: true });
                        setShowTermsAndConditionsModal(false);
                    }}
                />
            </ActionModal>
        </>
    );
};

export default CompanyRegistration;
